import type { SupabaseClient } from "@supabase/supabase-js";

// Assignment write path + assignable-member picker for the Schedule Builder
// (CONSOLE-SCHEDULE-ASSIGN, console-safe revision per Codex 1ce863f). The ONLY write is
// the hosted RPC public.assign_member_to_shift_slot, which carries MEMBERSHIP ids - the
// console never reads, sends, or renders raw auth user ids. The picker reads only the
// redacted management_member_directory view and renders member_label / member_display_name.

export const ASSIGNABLE_SHIFT_ROLES = ["guard", "supervisor", "admin"] as const;
export type AssignableShiftRole = (typeof ASSIGNABLE_SHIFT_ROLES)[number];

export interface AssignableMember {
  guardMembershipId: string;
  /** Redacted display label from management_member_directory - never a raw id. */
  guardLabel: string;
  /** Explicit display name (member_display_name) when set; primary label for the picker. */
  guardDisplayName: string | null;
  /** Short reference hash (member_ref) for hover only. */
  guardRef: string | null;
  role: AssignableShiftRole;
}

/** @deprecated Use AssignableMember */
export type AssignableGuard = AssignableMember;

const MEMBER_PICKER_LIMIT = 100;
const FALLBACK_MEMBER_LABEL = "Member (unlabeled)";

function isAssignableRole(value: unknown): value is AssignableShiftRole {
  return typeof value === "string" && (ASSIGNABLE_SHIFT_ROLES as readonly string[]).includes(value);
}

/** Active guards, supervisors, and admins for the assign picker. Throws safe. */
export async function fetchAssignableMembers(
  client: SupabaseClient,
  accountId: string,
): Promise<AssignableMember[]> {
  const { data, error } = await client
    .from("management_member_directory")
    .select("membership_id, member_label, member_display_name, member_ref, role, status")
    .eq("account_id", accountId)
    .in("role", [...ASSIGNABLE_SHIFT_ROLES])
    .eq("status", "active")
    .order("created_at", { ascending: true })
    .limit(MEMBER_PICKER_LIMIT);
  if (error !== null || !Array.isArray(data)) {
    throw new Error("Could not load the member picker.");
  }
  const members: AssignableMember[] = [];
  for (const raw of data as Array<Record<string, unknown>>) {
    const guardMembershipId = typeof raw.membership_id === "string" ? raw.membership_id : null;
    const role = raw.role;
    if (guardMembershipId === null || !isAssignableRole(role)) {
      continue;
    }
    const displayName =
      typeof raw.member_display_name === "string" && raw.member_display_name.trim().length > 0
        ? raw.member_display_name.trim()
        : null;
    const label =
      typeof raw.member_label === "string" && raw.member_label.length > 0
        ? raw.member_label
        : FALLBACK_MEMBER_LABEL;
    members.push({
      guardMembershipId,
      guardLabel: label,
      guardDisplayName: displayName,
      guardRef:
        typeof raw.member_ref === "string" && raw.member_ref.trim().length > 0 ? raw.member_ref.trim() : null,
      role,
    });
  }
  return members.sort((a, b) =>
    (a.guardDisplayName ?? a.guardLabel).localeCompare(b.guardDisplayName ?? b.guardLabel),
  );
}

/** @deprecated Use fetchAssignableMembers */
export const fetchAssignableGuards = fetchAssignableMembers;

/** Single safe generic for assignment failures - raw RPC errors never render. */
export const SAFE_ASSIGN_FAILURE = "Couldn't assign that member. Please try again.";

const KNOWN_ASSIGN_FAILURES: ReadonlySet<string> = new Set([
  "Guard membership is required for assignment.",
  "Only published shift slots can receive guard assignments.",
  "Shift slot is already fully assigned.",
  "Guard already has an overlapping scheduled or active route assignment.",
  "Member already has an overlapping scheduled or active route assignment.",
  "Active guard membership was not found for this account.",
  "Active assignable membership was not found for this account.",
  "Caller is not authorized to manage schedules for this account.",
]);

export type AssignOutcome =
  | {
      ok: true;
      routeAssignmentId: string;
      assignmentStatus: string;
      credentialWarningTypes: string[];
    }
  | { ok: false; reason: string };

/** Assign a membership to a published shift slot via the hosted RPC. Never throws. */
export async function assignMemberToShiftSlot(
  client: SupabaseClient,
  accountId: string,
  shiftSlotId: string,
  guardMembershipId: string,
): Promise<AssignOutcome> {
  try {
    const { data, error } = await client.rpc("assign_member_to_shift_slot", {
      p_account_id: accountId,
      p_shift_slot_id: shiftSlotId,
      p_guard_membership_id: guardMembershipId,
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return { ok: false, reason: KNOWN_ASSIGN_FAILURES.has(message) ? message : SAFE_ASSIGN_FAILURE };
    }
    const row = Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
    if (row === undefined || typeof row.route_assignment_id !== "string") {
      return { ok: false, reason: SAFE_ASSIGN_FAILURE };
    }
    return {
      ok: true,
      routeAssignmentId: row.route_assignment_id,
      assignmentStatus: typeof row.assignment_status === "string" ? row.assignment_status : "scheduled",
      credentialWarningTypes: Array.isArray(row.credential_warning_types)
        ? row.credential_warning_types.filter((value): value is string => typeof value === "string")
        : [],
    };
  } catch {
    return { ok: false, reason: SAFE_ASSIGN_FAILURE };
  }
}
